import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2 as Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  Button,
  Icon,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Link as LinkIcon, Public as PublicIcon, Business as BusinessIcon } from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import * as MuiIcons from '@mui/icons-material';
import pagesService from '../../services/pagesService';
import { useThemeCustom } from '../../hooks/useThemeCustom';

/**
 * PageTypeSelector - Modal para seleção de tipo de página
 * 
 * Apresenta opções de tipos de páginas dinamicamente carregadas da API
 * Mantém visual idêntico ao componente estático anterior
 */
export default function PageTypeSelector({ open, onClose }) {
  const navigate = useNavigate();
  const [pageTypes, setPageTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getLabel } = useThemeCustom();

  // Mapeamento de ícones Material-UI por nome
  const iconMap = {
    'Link': LinkIcon,
    'Business': BusinessIcon,
    'Person': PublicIcon,
    'default': LinkIcon
  };

  // Carregar tipos de página quando modal abre
  useEffect(() => {
    if (open) {
      loadPageTypes();
    }
  }, [open]);

  const loadPageTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pagesService.getPageTypes();
      
      // Mapeamento de labels dinâmicos baseados no template ativo
      const dynamicLabels = {
        'clinic': {
          name: `Página de ${getLabel('places')}`,
          description: `Página profissional com informações sobre seu ${getLabel('places').toLowerCase()}, especialidades e equipe`
        },
        'professional': {
          name: `Página de ${getLabel('professionals')}`,
          description: `Crie uma página pessoal com sua experiência, especialidades e agenda de atendimentos`
        }
      };
      
      // Transformar resposta da API para formato interno
      const formatted = (data && Array.isArray(data) ? data : (data?.$values || [])).map(pt => {
        // Usar labels dinâmicos se disponíveis para este tipo
        const dynamicLabel = dynamicLabels[pt.type];
        
        return {
          id: pt.type, // Use 'type' como identificador da rota
          name: dynamicLabel?.name || pt.name,
          description: dynamicLabel?.description || pt.description || '',
          icon: iconMap[pt.iconName] || iconMap['default'],
          enabled: pt.isActive,
          comingSoon: !pt.isActive,
          color: pt.isActive ? '#1976d2' : '#999',
          linkLabel: pt.linkLabel || (pt.isActive ? 'Criar →' : 'EM BREVE'),
          displayOrder: pt.displayOrder,
          type: pt.type, // Adicionar type para comparação
          beta: pt.type === 'professional',
        };
      }).sort((a, b) => a.displayOrder - b.displayOrder);
      
      setPageTypes(formatted);
    } catch (err) {
      console.error('Erro ao carregar tipos de página:', err);
      setError('Não foi possível carregar os tipos de página');
      // Fallback: usar tipos estáticos em caso de erro - com labels dinâmicos
      setPageTypes([
        {
          id: 'link_in_bio',
          name: 'Link in Bio',
          description: 'Crie uma página personalizável com links para suas redes sociais, agenda e outras informações',
          icon: LinkIcon,
          enabled: true,
          color: '#1976d2',
          linkLabel: 'Criar →',
          displayOrder: 1,
          type: 'link_in_bio'
        },
        {
          id: 'clinic',
          name: `Página de ${getLabel('places')}`,
          description: `Página profissional com informações sobre seu ${getLabel('places').toLowerCase()}, especialidades e equipe`,

          icon: BusinessIcon,
          enabled: true,
          color: '#1976d2',
          linkLabel: 'Criar →',
          displayOrder: 2,
          type: 'clinic'
        },
        {
          id: 'professional',
          name: `Página de ${getLabel('professionals')}`,
          description: 'Crie uma página pessoal com sua experiência, especialidades e agenda de atendimentos',
          icon: PublicIcon,
          enabled: true,
          color: '#1976d2',
          linkLabel: 'Criar →',
          displayOrder: 3,
          type: 'professional',
          beta: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (type) => {
    if (type.enabled) {
      onClose();
      // Normalizar ID para usar na rota (link_in_bio -> link_in_bio)
      const routeId = type.id.replace('_', '-');
      navigate(`/pages/new/${type.id}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Criar Nova Página
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Selecione o tipo de página que deseja criar
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error} - Usando opções padrão
          </Alert>
        ) : null}

        <Grid container spacing={3}>
          {pageTypes.map((type) => (
            <Grid size={{ xs: 12, sm: 6 }} key={type.id}>
              <Card
                sx={{
                  height: '100%',
                  cursor: type.enabled ? 'pointer' : 'not-allowed',
                  opacity: type.enabled ? 1 : 0.6,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: type.enabled ? 3 : 1,
                    transform: type.enabled ? 'translateY(-4px)' : 'none',
                    bgcolor: type.enabled ? 'action.hover' : 'action.disabled'
                  },
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <CardActionArea
                  disabled={!type.enabled}
                  onClick={() => handleSelectType(type)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    {/* Icon */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <type.icon
                        sx={{
                          fontSize: 48,
                          color: type.color,
                          opacity: type.enabled ? 1 : 0.5
                        }}
                      />
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: type.enabled ? 'text.primary' : 'text.secondary'
                      }}
                    >
                      {type.name}
                      {type.beta && (
                        <Chip
                          label="BETA"
                          size="small"
                          color="info"
                          sx={{ ml: 1, fontSize: '0.65rem', height: 20, fontWeight: 700, verticalAlign: 'middle' }}
                        />
                      )}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        mb: 2,
                        lineHeight: 1.6,
                        minHeight: '3em'
                      }}
                    >
                      {type.description}
                    </Typography>

                    {/* Coming Soon Badge ou Action Button */}
                    {!type.enabled && (
                      <Box sx={{ mt: 2 }}>
                        <Tooltip title="Funcionalidade em desenvolvimento — disponível em breve">
                          <span style={{ cursor: 'not-allowed', display: 'inline-block' }}>
                            <Button
                              variant="outlined"
                              disabled
                              size="small"
                              sx={{
                                cursor: 'not-allowed',
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            >
                              {type.linkLabel}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    )}

                    {type.enabled && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            display: 'block',
                            textTransform: 'uppercase'
                          }}
                        >
                          {type.linkLabel}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
